// =============================================================================
// clientes/src/paginas/Carrinho.tsx — revisar e confirmar o pedido.
//
// O preço mostrado é o do catálogo NO MOMENTO em que o item entrou — exibição.
// Quem congela o preço de verdade é criar_pedido_b2b, no servidor, que só
// recebe id + quantidade. A idempotency key nasce quando a tela abre e vale
// até o pedido sair: dois toques no botão = UM pedido.
// =============================================================================
import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { FormaPagamentoB2B } from '@core/types';
import Aviso from '../componentes/Aviso';
import {
  lerCarrinho, limparCarrinho, mudarQuantidade, paraItensDePedido, remover,
  salvarCarrinho, temSobEncomenda, totalDoCarrinho, type ItemCarrinho,
} from '../lib/carrinho';
import { criarPedido } from '../lib/consultas';
import { brl, qtd } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

const FORMAS: ReadonlyArray<{ id: FormaPagamentoB2B; rotulo: string }> = [
  { id: 'pix', rotulo: 'Pix' },
  { id: 'dinheiro', rotulo: 'Dinheiro' },
  { id: 'debito', rotulo: 'Débito' },
  { id: 'credito', rotulo: 'Crédito' },
  { id: 'boleto', rotulo: 'Boleto' },
  { id: 'faturado', rotulo: 'Faturado (a prazo)' },
];

export default function Carrinho() {
  const navegar = useNavigate();
  const [lista, setLista] = useState<ItemCarrinho[]>(lerCarrinho);
  const [forma, setForma] = useState<FormaPagamentoB2B>('pix');
  const [observacao, setObservacao] = useState('');
  // Uma chave por "sessão de carrinho": o duplo toque devolve o MESMO pedido.
  const chave = useMemo(() => crypto.randomUUID(), []);

  const aplicar = (nova: ItemCarrinho[]) => { setLista(nova); salvarCarrinho(nova); };

  const confirmar = useMutation({
    mutationFn: () => criarPedido({
      itens: paraItensDePedido(lista),
      forma,
      observacao: observacao.trim() || undefined,
      idempotencyKey: chave,
    }),
    onSuccess: () => {
      limparCarrinho();
      navegar('/pedidos', { state: { confirmado: true } });
    },
  });

  if (lista.length === 0) {
    return (
      <p className="text-sm text-dim">
        Carrinho vazio — escolha as peças no catálogo.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <ul className="space-y-2">
        {lista.map((i) => (
          <li key={i.product_id} className="cartao p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-mono text-[0.625rem] uppercase text-dim">{i.sku}</p>
                <p className="text-sm font-bold leading-snug">{i.descricao}</p>
                {!i.pronta_entrega && (
                  <p className="text-xs font-semibold text-alerta">
                    sob encomenda — a loja confirma o prazo
                  </p>
                )}
              </div>
              <button type="button" className="text-xs text-erro underline"
                      onClick={() => aplicar(remover(lista, i.product_id))}>
                tirar
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button type="button" className="botao-secundario px-3 py-1 text-sm"
                        aria-label="Diminuir"
                        onClick={() => aplicar(mudarQuantidade(lista, i.product_id, i.quantidade - 1))}>
                  −
                </button>
                <span className="min-w-8 text-center tabular-nums text-sm font-bold">
                  {qtd(i.quantidade)}
                </span>
                <button type="button" className="botao-secundario px-3 py-1 text-sm"
                        aria-label="Aumentar"
                        onClick={() => aplicar(mudarQuantidade(lista, i.product_id, i.quantidade + 1))}>
                  +
                </button>
              </div>
              <span className="tabular-nums text-sm font-black">
                {brl(i.preco * i.quantidade)}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {temSobEncomenda(lista) && (
        <Aviso tipo="alerta">
          Itens marcados <strong>sob encomenda</strong> não estão na prateleira —
          a loja encomenda do fornecedor e confirma o prazo com você.
        </Aviso>
      )}

      <div className="cartao space-y-3 p-4">
        <div className="flex items-baseline justify-between">
          <span className="rotulo">Total</span>
          <span className="tabular-nums text-xl font-black">{brl(totalDoCarrinho(lista))}</span>
        </div>
        <p className="text-xs text-dim">
          O valor fecha na confirmação da loja — se algum preço tiver mudado no
          catálogo, vale o do pedido confirmado.
        </p>
        <div>
          <label className="rotulo" htmlFor="forma">Como pretende pagar</label>
          <select id="forma" className="campo" value={forma}
                  onChange={(e) => setForma(e.target.value as FormaPagamentoB2B)}>
            {FORMAS.map((f) => <option key={f.id} value={f.id}>{f.rotulo}</option>)}
          </select>
          <p className="mt-1 text-xs text-dim">
            É um combinado, não uma cobrança — o acerto é com a loja, na
            entrega ou retirada.
          </p>
        </div>
        <div>
          <label className="rotulo" htmlFor="obs">Observação (opcional)</label>
          <textarea id="obs" className="campo" rows={2} value={observacao}
                    placeholder="ex.: entregar na oficina até quinta"
                    onChange={(e) => setObservacao(e.target.value)} />
        </div>
        {confirmar.error ? <Aviso tipo="erro">{mensagemDeErro(confirmar.error)}</Aviso> : null}
        <button type="button" className="botao-primario w-full py-2.5 text-sm"
                disabled={confirmar.isPending}
                onClick={() => confirmar.mutate()}>
          {confirmar.isPending ? 'Enviando…' : 'Confirmar pedido'}
        </button>
      </div>
    </div>
  );
}
